import { useId, useMemo, useState, type FormEvent } from "react";
import { ApiError } from "../../../lib/apiClient";
import { createSharedMailbox } from "../../../lib/mailAdminApi";
import type {
  MailAdminCandidateDto,
  MailAdminMailboxDto,
} from "../../../lib/mailAdminTypes";
import {
  MAIL_DOMAINS,
  filterCandidates,
  validateLocalPart,
} from "../../../lib/mailAdminValidation";
import { Checkbox } from "../../FormCheck";
import { AdminDialog, FieldStack } from "./AdminDialog";

export function CreateSharedMailboxDialog({
  candidates,
  onClose,
  onCreated,
}: {
  candidates: readonly MailAdminCandidateDto[];
  onClose: () => void;
  onCreated: (mailbox: MailAdminMailboxDto) => void;
}) {
  const formId = useId();
  const [localPart, setLocalPart] = useState("");
  const [domain, setDomain] = useState<string>(MAIL_DOMAINS[0]);
  const [displayName, setDisplayName] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const localError = validateLocalPart(localPart);
  const showLocalError = (touched || localPart.length > 0) && localError;
  const valid = !localError && !!displayName.trim() && selected.length > 0;

  const visible = useMemo(
    () => filterCandidates(candidates, { query }),
    [candidates, query],
  );

  const toggle = (uid: string, on: boolean) =>
    setSelected((prev) =>
      on ? [...new Set([...prev, uid])] : prev.filter((u) => u !== uid),
    );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createSharedMailbox({
        localPart,
        domain,
        displayName: displayName.trim(),
        memberUids: selected,
      });
      onCreated(created);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create the mailbox.",
      );
      setBusy(false);
    }
  };

  return (
    <AdminDialog
      title="Create shared mailbox"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form={formId}
            className="btn btn-primary"
            disabled={!valid || busy}
          >
            {busy ? "Creating…" : "Create mailbox"}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={(e) => void submit(e)} noValidate>
        <FieldStack>
          <label htmlFor={`${formId}-local`}>Address</label>
          <div
            style={{
              display: "flex",
              gap: "0.4rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              id={`${formId}-local`}
              value={localPart}
              placeholder="support"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={!!showLocalError}
              aria-describedby={`${formId}-local-msg`}
              onChange={(e) => setLocalPart(e.target.value.trim())}
              onBlur={() => setTouched(true)}
              style={{ flex: "1 1 10rem", minWidth: 0 }}
            />
            <span aria-hidden="true">@</span>
            <select
              aria-label="Domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              style={{ flex: "0 0 auto" }}
            >
              {MAIL_DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div
            id={`${formId}-local-msg`}
            className="form-hint"
            aria-live="polite"
          >
            {showLocalError
              ? localError
              : localPart
                ? `Address: ${localPart}@${domain}`
                : "Lowercase letters, digits, dot, underscore, hyphen (1-64)."}
          </div>

          <label htmlFor={`${formId}-name`}>Display name</label>
          <input
            id={`${formId}-name`}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Support team"
            autoComplete="off"
          />

          <label htmlFor={`${formId}-search`}>Members</label>
          <input
            id={`${formId}-search`}
            type="search"
            value={query}
            placeholder="Search by name or email"
            onChange={(e) => setQuery(e.target.value)}
          />
          <div
            role="group"
            aria-label="Select members"
            style={{
              maxHeight: 200,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "0.35rem",
            }}
          >
            {visible.length === 0 ? (
              <div className="form-hint">No users match.</div>
            ) : (
              visible.map((c) => (
                <Checkbox
                  key={c.uid}
                  checked={selected.includes(c.uid)}
                  onChange={(on) => toggle(c.uid, on)}
                  label={`${c.name} (${c.email})`}
                />
              ))
            )}
          </div>
          <div className="form-hint" aria-live="polite">
            {selected.length === 0
              ? "Pick at least one member. The first person you pick becomes the owner; roles can be adjusted later in the mailbox details."
              : `${selected.length} selected. The first person you picked is the owner; roles can be adjusted later in the mailbox details.`}
          </div>

          {error && (
            <div
              className="error-banner"
              role="alert"
              style={{ marginBottom: 0 }}
            >
              {error}
            </div>
          )}
        </FieldStack>
      </form>
    </AdminDialog>
  );
}
